import get from "lodash/get"
import axios from "axios"
import URITemplate from "urijs/src/URITemplate"
import * as ActionTypes from "constants-lib/actionTypes"
import { generateUniqueID } from "utils-lib/requests"
import { profileIdForAPIs } from "constants-lib/profileIdForAPIs"
import { mapToSnakeFromCamel } from "utils-lib/objects/mapToSnakeFromCamel"
import {
  getContactOptions,
  getCustomerContacts,
  getTokenAction,
} from "actions-lib"
import { AuthoringUtils } from "@adobe/aem-spa-page-model-manager"
import { urls } from "utils-lib/builds"
import dayjs from "utils-lib/date/index.js"

export const pendingAC = (data = null) => ({
  type: ActionTypes.SET_CUSTOMER_CONTACTS_PENDING,
  payload: data,
})

export const failedAC = (data) => ({
  type: ActionTypes.SET_CUSTOMER_CONTACTS_FAILED,
  payload: data,
})

export const fulfilledAC = (data) => ({
  type: ActionTypes.SET_CUSTOMER_CONTACTS_FULFILLED,
  payload: data,
})

export const setCustomerContactsMyWm =
  (customerId, token, contactInfo, successCB = () => {}) =>
  (dispatch) => {
    const template = URITemplate(urls.cloudfront.myPreferences.contacts.url)
    const putUrl = template.expand({
      ezpayId: customerId,
    })
    const apiType = `PUT_CONTACTS`
    const profileId = profileIdForAPIs().profileId[apiType]
    dispatch(pendingAC())

    const putOptions = {
      url: putUrl,
      headers: {
        "Request-Tracking-Id": generateUniqueID(),
        Authorization: `Bearer ${token}`,
        ProfileId: profileId,
      },
      method: `PUT`,
      data: contactInfo,
    }

    return axios(putOptions)
      .then((response) => {
        successCB()

        const statusCode = get(response, `status`, ``)
        const verificationTokenPref = get(response, `headers.token`, ``)

        //  refresh the list of contacts on left side
        dispatch(
          getCustomerContacts(customerId, token, () => {
            dispatch(
              getContactOptions(customerId, token, () => {
                dispatch(
                  fulfilledAC({
                    customerContacts: contactInfo,
                    verificationTokenPref,
                    statusCode,
                  }),
                )
              }),
            )
          }),
        )

        //  update contacts
        return response
      })
      .catch((response) => {
        const statusCode = get(response, `response.status`, ``)
        const errorMessage = get(
          response,
          `response.data.verificationErrorMsg`,
          ``,
        )
        const customerContacts = get(response, `response.data`)
        dispatch(
          failedAC({
            statusCode,
            errorMessage,
            customerContacts,
          }),
        )
      })
  }

export const setCustomerContacts =
  (customerId, contactInfo, successCB = () => {}) =>
  async (dispatch, getState) => {
    const api = `MANAGE_CONTACTS`
    const template = URITemplate(urls.url[api])
    const putUrl = template.expand({
      customerId,
    })
    dispatch(pendingAC())

    const mappedRequestData = mapToSnakeFromCamel(contactInfo)

    const { customerContacts } = getState()
    const oldContacts = customerContacts?.data?.customerContacts?.contacts
    const newContactsPartial = contactInfo?.contacts

    const newContactsFull = [
      {
        ...oldContacts.find((contact) => contact.type === `billing`),
        ...newContactsPartial.find((contact) => contact.type === `billing`),
      },
      {
        ...oldContacts.find((contact) => contact.type === `service`),
        ...newContactsPartial.find((contact) => contact.type === `service`),
      },
    ]

    const token = await getTokenAction(dispatch, getState)

    try {
      let response
      if (AuthoringUtils.isInEditor()) {
        const importedMockFile = await import(
          `./mocks/setCustomerContactsResponse.js`
        )
        response = importedMockFile.default
      } else {
        response = await axios.put(putUrl, mappedRequestData, {
          headers: {
            "Request-Tracking-Id": generateUniqueID(),
            Authorization: `Bearer ${token.accessToken}`,
          },
        })
      }

      successCB()
      dispatch(fulfilledAC(newContactsFull))

      return response
    } catch (response) {
      // console.log(`response error`, response)
      const statusCode = get(response, `response.status`, ``)
      const errorMessage = get(
        response,
        `response.data.verificationErrorMsg`,
        ``,
      )

      dispatch(
        failedAC({
          statusCode,
          errorMessage,
          customerContacts: get(response, `response.data`),
        }),
      )

      return statusCode
    }
  }

export const sendOTP =
  (userId, customerId, locale, successCB = () => {}) =>
  async (dispatch, getState) => {
    const api = `SEND_OTP`
    const apiKey = get(urls, `apiKey.USER[${api}]`, ``)
    const template = URITemplate(urls.url[api])
    const getUrl = template.expand({
      userId,
      customerId,
      locale,
    })

    const token = await getTokenAction(dispatch, getState)

    try {
      let response
      if (AuthoringUtils.isInEditor()) {
        const importedMockFile = await import(`./mocks/sendOTPResponse.js`)
        response = importedMockFile.default
      } else {
        response = await axios.get(getUrl, {
          headers: {
            oktaToken: token.accessToken,
            apiKey,
          },
        })
      }

      successCB()
      return response
    } catch (response) {
      const status = get(response, `response.status`, ``)
      return status
    }
  }

export const verifyOTP =
  (userId, customerId, email, otp, locale, successCB = () => {}) =>
  async (dispatch, getState) => {
    const api = `VERIFY_OTP`
    const apiKey = get(urls, `apiKey.USER[${api}]`, ``)
    const template = URITemplate(urls.url[api])
    const postUrl = template.expand({
      userId,
      customerId,
    })

    const token = await getTokenAction(dispatch, getState)

    try {
      let response
      if (AuthoringUtils.isInEditor()) {
        const importedMockFile = await import(`./mocks/verifyOTPResponse.js`)
        response = importedMockFile.default
      } else {
        response = await axios.post(
          postUrl,
          { email, otp, locale },
          {
            headers: {
              oktaToken: token.accessToken,
              apiKey,
            },
          },
        )
      }

      successCB()
      return response.status
    } catch (response) {
      // console.log(`response error`, response)
      const status = get(response, `response.status`, ``)
      return status
    }
  }

export const sendAccountUpdateEmail =
  (userId, customerId, emails, changeDetected, locale) =>
  async (dispatch, getState) => {
    const api = `SEND_ACCOUNT_UPDATE_EMAIL`
    const apiKey = get(urls, `apiKey.USER[${api}]`, ``)
    const template = URITemplate(urls.url[api])
    const postUrl = template.expand({
      userId,
      customerId,
    })

    const token = await getTokenAction(dispatch, getState)
    const data = {
      changeDetected,
      dateChangeMade: dayjs(new Date())?.format(`MM/DD/YYYY`),
      emails,
      locale,
    }

    try {
      let response
      if (AuthoringUtils.isInEditor()) {
        const importedMockFile = await import(
          `./mocks/sendAccountUpdateEmailResponse.js`
        )
        response = importedMockFile.default
      } else {
        response = await axios.post(postUrl, data, {
          headers: {
            oktaToken: token.accessToken,
            apiKey,
          },
        })
      }

      return response.status
    } catch (response) {
      const status = get(response, `response.status`, ``)
      return status
    }
  }
